import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  EyeIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { workshopAPI } from '../../services/api';
import { useToast } from '../Toast';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDateToIST, formatDateTimeToIST, formatTimeToIST } from '../../utils/dateUtils';

const WorkshopAttendance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { checkPermission, Permission } = usePermissions();
  const [workshop, setWorkshop] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  });

  const limit = 20;

  useEffect(() => {
    if (!id) {
      showToast('Invalid workshop ID', 'error');
      navigate('/workshops');
      return;
    }
    
    fetchWorkshop();
    fetchAttendance();
  }, [id, currentPage, filterDate, filterStatus, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check permissions after hooks
  if (!checkPermission(Permission.MANAGE_WORKSHOP_ATTENDANCE) && !checkPermission(Permission.VIEW_WORKSHOP_ATTENDANCE)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl p-8 max-w-md mx-4 text-center">
          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <DocumentTextIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h1>
          <p className="text-gray-300 mb-6">
            You don't have permission to view workshop attendance.
          </p>
          <button
            onClick={() => navigate(`/workshops/${id}`)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Back to Workshop
          </button>
        </div>
      </div>
    );
  }

  const fetchWorkshop = async () => {
    try {
      setLoading(true);
      const response = await workshopAPI.getById(id);
      setWorkshop(response.data);
    } catch (error) {
      console.error('Error fetching workshop:', error);
      showToast('Failed to load workshop details', 'error');
      navigate('/workshops');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      setAttendanceLoading(true);
      const skip = (currentPage - 1) * limit;
      const response = await workshopAPI.getAttendance(id, skip, limit);
      setAttendanceRecords(response.data.attendance_records);
      setTotalRecords(response.data.total);
      
      // Calculate stats
      calculateStats(response.data.attendance_records);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      showToast('Failed to load attendance records', 'error');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const calculateStats = (records) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const stats = {
      total: records.length,
      today: 0,
      thisWeek: 0,
      thisMonth: 0
    };

    records.forEach(record => {
      const recordDate = new Date(record.session_date);
      if (recordDate >= today) {
        stats.today++;
      }
      if (recordDate >= weekAgo) {
        stats.thisWeek++;
      }
      if (recordDate >= monthAgo) {
        stats.thisMonth++;
      }
    });

    setStats(stats);
  };

  const formatDate = (dateString) => {
    return formatDateToIST(dateString);
  };

  const formatTime = (dateString) => {
    return formatTimeToIST(dateString);
  };

  const formatDateTime = (dateString) => {
    return formatDateTimeToIST(dateString);
  };

  const getStatusIcon = (isDuplicate) => {
    if (isDuplicate) {
      return <XCircleIcon className="h-5 w-5 text-orange-500" />;
    }
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
  };

  const getStatusText = (isDuplicate) => {
    if (isDuplicate) {
      return 'Duplicate';
    }
    return 'Marked';
  };

  const getStatusColor = (isDuplicate) => {
    if (isDuplicate) {
      return 'text-orange-600 bg-orange-100';
    }
    return 'text-green-600 bg-green-100';
  };

  const filteredRecords = attendanceRecords.filter(record => {
    let matches = true;
    
    // Filter by date
    if (filterDate) {
      const recordDate = new Date(record.session_date).toLocaleDateString('en-CA', {
        timeZone: 'Asia/Kolkata'
      });
      matches = matches && recordDate === filterDate;
    }
    
    // Filter by status
    if (filterStatus !== 'all') {
      const isDuplicate = record.is_duplicate || false;
      if (filterStatus === 'duplicate') {
        matches = matches && isDuplicate;
      } else if (filterStatus === 'valid') {
        matches = matches && !isDuplicate;
      }
    }
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const studentName = record.name || '';
      const phone = record.phone_verified || '';
      const course = record.course || '';
      const year = record.year || '';
      matches = matches && (
        studentName.toLowerCase().includes(searchLower) ||
        phone.includes(searchTerm) ||
        course.toLowerCase().includes(searchLower) ||
        year.toLowerCase().includes(searchLower)
      );
    }
    
    return matches;
  });

  const totalPages = Math.ceil(totalRecords / limit);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/workshops/${id}`)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Workshop Attendance</h1>
            <p className="text-gray-400 mt-1">{workshop?.name}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`/workshops/${id}/attendance-qr`)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <DocumentTextIcon className="h-4 w-4" />
            QR Code
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Total Records</p>
              <p className="text-2xl font-bold text-white">{totalRecords}</p>
            </div>
            <DocumentTextIcon className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Today</p>
              <p className="text-2xl font-bold text-white">{stats.today}</p>
            </div>
            <CalendarIcon className="h-8 w-8 text-green-400" />
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">This Week</p>
              <p className="text-2xl font-bold text-white">{stats.thisWeek}</p>
            </div>
            <ClockIcon className="h-8 w-8 text-yellow-400" />
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">This Month</p>
              <p className="text-2xl font-bold text-white">{stats.thisMonth}</p>
            </div>
            <UserIcon className="h-8 w-8 text-purple-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Search
            </label>
            <input
              type="text"
              placeholder="Search by name, phone, course, or year..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Date
            </label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Records</option>
              <option value="valid">Valid Attendance</option>
              <option value="duplicate">Duplicate Records</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterDate('');
                setFilterStatus('all');
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <FunnelIcon className="h-4 w-4" />
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Attendance Records */}
      <div className="bg-gray-800 rounded-lg border border-gray-700">
        <div className="p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Attendance Records</h2>
        </div>
        
        {attendanceLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-400 mt-2">Loading attendance records...</p>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentTextIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">No attendance records found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Course & Year
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredRecords.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-700">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-white">
                            {record.name || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-400">
                            ID: {record.registration_id}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-300">
                          {record.phone_verified}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm text-gray-300">
                          {record.course} - {record.year}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-300">
                          {formatDate(record.session_date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-300">
                          {formatTime(record.marked_at)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(record.is_duplicate)}
                        <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(record.is_duplicate)}`}>
                          {getStatusText(record.is_duplicate)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          // Show detailed view or modal
                          showToast(`Attendance marked at ${formatDateTime(record.marked_at)}`, 'info');
                        }}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalRecords)} of {totalRecords} records
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white rounded-lg transition-colors"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-gray-300">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white rounded-lg transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkshopAttendance; 